import ContentLayout from '@/components/layout/ContentLayout';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useAuth } from '@/contexts/AuthContext';
import { useGamification } from '@/contexts/GamificationContext';
import { getAssignmentListByStudent, getQuestionList, setSelfAssessmentResult, getStudentSummary, getQuestionInfo, submitSelfAssessment } from '@/services/generatedAPIs';
import { AssignmentModel, AssignmentStats } from '@/services/generatedAPIs.schemas';
import { flyKpToHeader, flyGsToHeader } from '@/components/gamification/gamificationEffects';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAsyncAction } from '@/hooks/use-async-action';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CalendarIcon, TrendingDown, Minus, ArrowRight, ChevronDown, Sparkles } from 'lucide-react';
import selectTaskIcon from '@/assets/student/self-assess/select-task.svg?raw';
import subjectBookIcon from '@/assets/student/self-assess/subject-book.svg?raw';
import deadlineCalIcon from '@/assets/student/self-assess/deadline-cal.svg?raw';
import submitCalIcon from '@/assets/student/self-assess/submit-cal.svg?raw';
import snapshotIcon from '@/assets/student/self-assess/snapshot.svg?raw';
import scoreIcon from '@/assets/student/self-assess/score.svg?raw';
import timeIcon from '@/assets/student/self-assess/time.svg?raw';
import knowledgeIcon from '@/assets/student/self-assess/knowledge.svg?raw';
import trendUpIcon from '@/assets/student/self-assess/trend-up.svg?raw';
import sparkleIcon from '@/assets/student/self-assess/sparkle.svg?raw';
import { cn, questionMistakeI18N } from '@/lib/utils';
import { generateAnswerSessionToken } from '@/lib/answerSession';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ROUTE } from '@/routes/constants';
import { useMobileLandscape } from '@/components/mobile-landscape-answer';
import MobileLandscapeSelfAssess from '@/components/mobile-landscape-self-assess';

export interface CompletedAssignment {
    assignment: AssignmentModel;
    stats: AssignmentStats;
    totalCorrect: number;
    totalQuestions: number;
    totalDuration: number; // in seconds
    completionDate?: Date; // latest submission date
    knowledgePoints?: string[]; // unique knowledge points from questions
    mistakedKnowledgePoints?: string[]; // knowledge points from incorrectly answered questions
    primaryMistakes?: string[]; // primary mistakes related to knowledge points
}

interface SelfAssessmentTimestamps {
    started_at?: string;          // When user clicks "Start Interactive Self-Assessment"
    step_1_completed_at?: string; // When user clicks Next on step 1
    step_2_completed_at?: string; // When user clicks Next on step 2
    step_3_completed_at?: string; // When user clicks Next on step 3
    step_4_completed_at?: string; // When user clicks Next on step 4 (phase summary)
    step_5_completed_at?: string; // When user clicks Next on step 5
    step_6_completed_at?: string; // When user clicks Complete on step 6 (final step)
}

export interface SelfAssessmentData {
    expected_correctness?: number;    // Ratio (0-1) of expected correct / total
    challenging_kp?: string;          // Textual input
    primary_mistakes?: string[];      // Array of selected mistake types
    efficiency_satisfaction?: number; // 1-5 scale
    frequent_solution?: string[];     // Array of selected solution options
    completed?: boolean;              // Whether the assessment is completed
    ai_feedback?: string;             // AI generated feedback
    timestamps?: SelfAssessmentTimestamps;
}

/** Returns current time as ISO 8601 string in Hong Kong timezone (UTC+8) */
const getHKTimestamp = (): string => {
    const now = new Date();
    const hkOffset = 8 * 60; // UTC+8 in minutes
    const localOffset = now.getTimezoneOffset(); // in minutes, negative if ahead of UTC
    const hkTime = new Date(now.getTime() + (hkOffset + localOffset) * 60000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${hkTime.getFullYear()}-${pad(hkTime.getMonth() + 1)}-${pad(hkTime.getDate())}T${pad(hkTime.getHours())}:${pad(hkTime.getMinutes())}:${pad(hkTime.getSeconds())}+08:00`;
};

export const MISTAKE_OPTIONS = [
    { key: 'Comprehension Error', translationKey: 'q3OptComprehension', explanationKey: 'q3ExpComprehension' },
    { key: 'Transformation Error', translationKey: 'q3OptTransformation', explanationKey: 'q3ExpTransformation' },
    { key: 'Mathematical Processing Error', translationKey: 'q3OptProcessing', explanationKey: 'q3ExpProcessing' },
    { key: 'Encoding Error', translationKey: 'q3OptEncoding', explanationKey: 'q3ExpEncoding' },
    { key: 'Unknown Error', translationKey: 'q3OptUnknown', explanationKey: 'q3ExpUnknown' },
];

export const SOLUTION_OPTIONS = [
    { key: 'A', translationKey: 'q5OptA' },
    { key: 'B', translationKey: 'q5OptB' },
    { key: 'C', translationKey: 'q5OptC' },
    { key: 'D', translationKey: 'q5OptD' },
    { key: 'E', translationKey: 'q5OptE' },
    { key: 'F', translationKey: 'q5OptF' },
];

const SLIDER_CLASS = 'w-full [&_[data-slot=slider-track]]:h-2.5 min-[1920px]:[&_[data-slot=slider-track]]:h-3 [&_[data-slot=slider-range]]:bg-[var(--practice-dialog-accent)] [&_[data-slot=slider-thumb]]:size-5 [&_[data-slot=slider-thumb]]:border-2 [&_[data-slot=slider-thumb]]:border-[var(--practice-dialog-accent)] min-[1920px]:[&_[data-slot=slider-thumb]]:size-7';

const CHECKBOX_CLASS = 'size-5 min-[1920px]:size-6 rounded-full border-[#E6E7EB] data-[state=checked]:bg-[var(--practice-dialog-accent)] data-[state=checked]:border-[var(--practice-dialog-accent)] data-[state=checked]:text-[var(--practice-dialog-accent-fg)]';

const CALENDAR_CLASSNAMES = {
    nav_button:
        'inline-flex size-7 cursor-pointer items-center justify-center rounded-[10px] bg-[var(--practice-dialog-accent)] p-0 text-[var(--practice-dialog-accent-fg)] opacity-100 transition hover:brightness-95',
    // 含選中元素的 cell 移除預設灰底(bg-accent)；range 連續段改由中間日按鈕淺綠承擔，單選時保持白底
    cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
    day_selected:
        'rounded-full! bg-[var(--practice-dialog-accent)] text-[var(--practice-dialog-accent-fg)] hover:bg-[var(--practice-dialog-accent)] hover:text-[var(--practice-dialog-accent-fg)] focus:bg-[var(--practice-dialog-accent)] focus:text-[var(--practice-dialog-accent-fg)]',
    day_range_start:
        'day-range-start rounded-full! aria-selected:bg-[var(--practice-dialog-accent)] aria-selected:text-[var(--practice-dialog-accent-fg)]',
    day_range_end:
        'day-range-end rounded-full! aria-selected:bg-[var(--practice-dialog-accent)] aria-selected:text-[var(--practice-dialog-accent-fg)]',
    day_range_middle:
        'rounded-none! bg-[color-mix(in_srgb,var(--practice-dialog-accent)_22%,white)] text-foreground aria-selected:bg-[color-mix(in_srgb,var(--practice-dialog-accent)_22%,white)] aria-selected:text-foreground',
};


export default function SelfAssessPage() {
    const { t } = useTranslation();
    const { getCurrentUser } = useAuth();
    const { addKp, addGs, kp, gs } = useGamification();
    const navigate = useNavigate();
    const [completedAssignments, setCompletedAssignments] = useState<CompletedAssignment[]>([]);
    const [selectedAssignmentId, setSelectedAssignmentId] = useState<string>('');
    const [stage, setStage] = useState<'select' | 'snapshot' | 'interactive' | 'completed'>('select');
    const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
    const [isLoading, setIsLoading] = useState(true);
    const [dateFilterOpen, setDateFilterOpen] = useState(false);
    const [assignmentDropdownOpen, setAssignmentDropdownOpen] = useState(false);
    const [snapshotAssignmentId, setSnapshotAssignmentId] = useState<string>(''); // Tracks which assignment is shown in snapshot
    const hasInitialized = useRef(false);
    const locationState = useLocation().state as { preSelectedAssignmentId?: number } | null;

    // Interactive self-assessment state
    const [interactiveStep, setInteractiveStep] = useState(1); // 1-6 (5 questions + summary), 7 = AI insights + gamification result
    const [selfAssessData, setSelfAssessData] = useState<SelfAssessmentData>({});
    const [expectedCorrectCount, setExpectedCorrectCount] = useState<number | null>(null);
    const [selectedMistakes, setSelectedMistakes] = useState<string[]>([]);
    const [challengingKp, setChallengingKp] = useState('');
    const [efficiencySatisfaction, setEfficiencySatisfaction] = useState<number | null>(null);
    const [frequentSolution, setFrequentSolution] = useState<string[]>([]);
    const [frequentSolutionOther, setFrequentSolutionOther] = useState('');
    const { isLoading: isSaving, execute: executeSave } = useAsyncAction({ showErrorToast: false });
    const { isLoading: isLoadingAiFeedback, execute: executeAiFeedback } = useAsyncAction({ showErrorToast: false });

    // Gamification state
    const [assignmentStatId, setAssignmentStatId] = useState<number | null>(null);
    const [gamificationAlreadySubmitted, setGamificationAlreadySubmitted] = useState(false);

    // 手机横屏(高≤500):走独立 844×390 FitScale 舞台(见 mobile-landscape-self-assess),复用本页全部逻辑/状态,桌面/平板不变
    const isMobileLandscape = useMobileLandscape();

    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const fetchAssignments = async () => {
            try {
                const rsp = await getAssignmentListByStudent({
                    studentId: getCurrentUser()!.id,
                    type: 'homework',
                });

                if (rsp.code === 200 && rsp.data) {
                    const { assignmentList, assignmentStatsList } = rsp.data;
                    const completed: CompletedAssignment[] = [];
                    const studentId = getCurrentUser()!.id;

                    for (let i = 0; i < assignmentList.length; i++) {
                        const stats = assignmentStatsList[i];
                        const assignmentId = assignmentList[i].assignment.id;

                        // Check if all questions are completed (submittedCount === totalCount)
                        if (stats.submittedCount !== stats.totalCount) continue;

                        // Fetch question-level data to get answerDurationInSecond and completionDate
                        let totalDuration = 0;
                        let completionDate: Date | undefined;
                        let primaryMistakes: [string, number][] = [];
                        try {
                            if (assignmentId) {
                                const questionRsp = await getQuestionList({
                                    studentId,
                                    assignmentId,
                                    newest: true,
                                });
                                if (questionRsp.code === 200 && questionRsp.data?.questionModelList) {
                                    totalDuration = questionRsp.data.questionModelList.reduce(
                                        (sum, q) => sum + (q.question?.answerDurationInSecond || 0),
                                        0
                                    );
                                    // Find the latest submission date
                                    const submissionDates = questionRsp.data.questionModelList
                                        .map(q => q.question?.submitedAt)
                                        .filter((d): d is string => !!d)
                                        .map(d => new Date(d));
                                    if (submissionDates.length > 0) {
                                        completionDate = new Date(Math.max(...submissionDates.map(d => d.getTime())));
                                    }

                                    const primaryMistakesMap: Map<string, number> = new Map();
                                    questionRsp.data.questionModelList.forEach((q) => {
                                        if (q.question?.errorPoints) {
                                            const errorPoints = q.question.errorPoints.split(',');
                                            errorPoints.forEach(point => {
                                                primaryMistakesMap.set(point, (primaryMistakesMap.get(point) || 0) + 1);
                                            });
                                        }
                                    });
                                    primaryMistakes = Array.from(primaryMistakesMap).sort((a, b) => {
                                        return b[1] - a[1];
                                    });
                                }
                            }
                        } catch (e) {
                            console.warn('Failed to fetch question data for assignment', assignmentId, e);
                        }

                        completed.push({
                            assignment: assignmentList[i],
                            stats,
                            totalCorrect: stats.correctCount,
                            totalQuestions: stats.totalCount,
                            totalDuration,
                            completionDate,
                            knowledgePoints: [], // Will be fetched when selected
                            primaryMistakes: primaryMistakes.map(pm => pm[0]),
                        });
                    }

                    // Sort by completion date (latest first)
                    completed.sort((a, b) => {
                        const dateA = a.completionDate?.getTime() || 0;
                        const dateB = b.completionDate?.getTime() || 0;
                        return dateB - dateA;
                    });

                    setCompletedAssignments(completed);

                    // Default to latest completed assignment, or use pre-selected from navigation state
                    if (completed.length > 0) {
                        const preSelectedId = locationState?.preSelectedAssignmentId?.toString();
                        const hasPreSelected = preSelectedId && completed.some(
                            (a) => a.assignment.assignment.id?.toString() === preSelectedId
                        );
                        setSelectedAssignmentId(
                            hasPreSelected ? preSelectedId : completed[0].assignment.assignment.id?.toString() || ''
                        );
                    }
                }
            } catch (error) {
                console.error('Error fetching assignments:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAssignments();
    }, [getCurrentUser]);

    const selectedAssignment = completedAssignments.find(
        (a) => a.assignment.assignment.id?.toString() === selectedAssignmentId
    );

    // Assignment shown in snapshot - only updates when button is clicked
    const snapshotAssignment = completedAssignments.find(
        (a) => a.assignment.assignment.id?.toString() === snapshotAssignmentId
    );

    // Previous assignment = the one completed just before the snapshot assignment (next in the list since sorted latest first)
    const snapshotIndex = completedAssignments.findIndex(
        (a) => a.assignment.assignment.id?.toString() === snapshotAssignmentId
    );
    const previousAssignment = snapshotIndex >= 0 && snapshotIndex < completedAssignments.length - 1
        ? completedAssignments[snapshotIndex + 1]
        : null;

    // Filter assignments by date range
    const filteredAssignments = completedAssignments.filter((a) => {
        if (!dateRange.from && !dateRange.to) return true;
        const dueDate = new Date(a.assignment.assignment.dueDate || 0);
        if (dateRange.from && dueDate < dateRange.from) return false;
        if (dateRange.to && dueDate > dateRange.to) return false;
        return true;
    });



    const getScoreComparison = () => {
        if (!snapshotAssignment || !previousAssignment) return null;

        const currentScore =
            (snapshotAssignment.totalCorrect / snapshotAssignment.totalQuestions) * 100;
        const previousScore =
            (previousAssignment.totalCorrect / previousAssignment.totalQuestions) * 100;
        const diff = currentScore - previousScore;

        return {
            currentScore,
            previousScore,
            diff,
            improved: diff > 0,
            declined: diff < 0,
        };
    };

    const comparison = getScoreComparison();

    const handleStartAssessment = async () => {
        if (selectedAssignmentId) {
            // Find the selected assignment
            const selected = completedAssignments.find(
                (a) => a.assignment.assignment.id?.toString() === selectedAssignmentId
            );
            if (!selected) return;

            // Reset all interactive assessment state
            setInteractiveStep(1);
            setExpectedCorrectCount(null);
            setChallengingKp('');
            setSelectedMistakes([]);
            setEfficiencySatisfaction(null);
            setFrequentSolution([]);
            setFrequentSolutionOther('');

            // Fetch fresh self-assessment data from backend
            try {
                const assignmentId = parseInt(selectedAssignmentId);
                const studentId = getCurrentUser()!.id;
                const questionRsp = await getQuestionList({
                    assignmentId,
                    studentId,
                    newest: true,
                });

                // Store assignment stat ID for gamification submission
                const statId = questionRsp.data?.assignmentStat?.id;
                if (statId) {
                    setAssignmentStatId(statId);
                    // Check if gamification self-assessment already submitted
                    setGamificationAlreadySubmitted(questionRsp.data?.assignmentStat?.selfAssessmentSubmitted === true);
                }


                if (questionRsp.code === 200 && questionRsp.data?.assignmentStat?.selfAssessmentJson) {
                    const existingData: SelfAssessmentData = JSON.parse(questionRsp.data.assignmentStat.selfAssessmentJson);
                    setSelfAssessData(existingData);
                    console.log('Fetched fresh self-assessment data:', existingData); // TODO: remove in production

                    // If assessment is already completed, show completed stage
                    if (existingData.completed === true) {
                        setSnapshotAssignmentId(selectedAssignmentId);
                        setStage('completed');
                        return;
                    }
                } else {
                    setSelfAssessData({});
                }

                // Fetch knowledge points for each question
                if (questionRsp.code === 200 && questionRsp.data?.questionModelList) {
                    const questionList = questionRsp.data.questionModelList;

                    // Separate correct and incorrect questions
                    const incorrectQuestionIds = questionList
                        .filter(q => q.question?.isCompleted && !q.question?.isCorrectAnswer)
                        .map(q => q.questionInfo?.id)
                        .filter((id): id is number => id !== undefined);

                    const allQuestionIds = questionList
                        .map(q => q.questionInfo?.id)
                        .filter((id): id is number => id !== undefined);

                    // Fetch knowledge points in parallel
                    const knowledgePointsSet = new Set<string>();
                    const mistakedKnowledgePointsMap = new Map<string, number>(); // Count occurrences

                    await Promise.all(
                        allQuestionIds.map(async (questionId) => {
                            try {
                                const infoRsp = await getQuestionInfo({ questionId });
                                if (infoRsp.code === 200 && infoRsp.data?.questionLabelList) {
                                    const isIncorrect = incorrectQuestionIds.includes(questionId);
                                    infoRsp.data.questionLabelList.forEach(label => {
                                        if (label.name && label.type === 'knowledge_point') {
                                            knowledgePointsSet.add(label.name);
                                            // Track mistakes
                                            if (isIncorrect) {
                                                const count = mistakedKnowledgePointsMap.get(label.name) || 0;
                                                mistakedKnowledgePointsMap.set(label.name, count + 1);
                                            }
                                        }
                                    });
                                }
                            } catch (e) {
                                console.warn('Failed to fetch question info for', questionId, e);
                            }
                        })
                    );

                    // Sort mistaked knowledge points by frequency
                    const sortedMistakedKPs = Array.from(mistakedKnowledgePointsMap.entries())
                        .sort((a, b) => b[1] - a[1])
                        .map(([kp]) => kp);

                    // Update the completed assignment with knowledge points
                    setCompletedAssignments(prev =>
                        prev.map(a =>
                            a.assignment.assignment.id?.toString() === selectedAssignmentId
                                ? {
                                    ...a,
                                    knowledgePoints: Array.from(knowledgePointsSet),
                                    mistakedKnowledgePoints: sortedMistakedKPs,
                                }
                                : a
                        )
                    );
                }
            } catch (e) {
                console.warn('Failed to fetch self-assessment data:', e);
                setSelfAssessData({});
            }

            setStage('snapshot');
            setSnapshotAssignmentId(selectedAssignmentId);
        }
    };

    const handleProceedInteractive = () => {
        setStage('interactive');
        // Record start timestamp
        const updatedData = {
            ...selfAssessData,
            timestamps: { ...selfAssessData.timestamps, started_at: getHKTimestamp() },
        };
        setSelfAssessData(updatedData);
        saveToBackend(updatedData);
        // If expected_correctness already exists in saved data, skip to step 2
        if (selfAssessData.expected_correctness !== undefined) {
            setInteractiveStep(2);
        } else {
            setInteractiveStep(1);
        }
    };

    // Validate current step response
    const isCurrentStepValid = (): boolean => {
        switch (interactiveStep) {
            case 1: // Q1: Expected correctness (scale)
                return expectedCorrectCount !== null;
            case 2: // Q2: Challenging KP (textual input - required)
                return challengingKp.trim().length > 0;
            case 3: // Q3: Primary mistakes (multiple select)
                return selectedMistakes.length > 0;
            case 4: // Phase summary - always valid
                return true;
            case 5: // Q4: Efficiency satisfaction (scale)
                return efficiencySatisfaction !== null;
            case 6: // Q5: Frequent solution (multiple select)
                if (frequentSolution.length === 0) return false;
                // If "Others" selected, must have text
                if (frequentSolution.some(s => s.startsWith('F.')) && frequentSolutionOther.trim().length === 0) return false;
                return true;
            default:
                return false;
        }
    };

    // Save self-assessment data to backend
    const saveToBackend = useCallback((data: SelfAssessmentData) => {
        if (!snapshotAssignment) return;
        const assignmentId = snapshotAssignment.assignment.assignment.id;
        const studentId = getCurrentUser()?.id;
        if (!assignmentId || !studentId) return;

        executeSave(async () => {
            try {
                const response = await setSelfAssessmentResult({
                    assignmentId,
                    studentId,
                    resultJson: JSON.stringify(data),
                });
                console.log("Save response:", response);
            } catch (error) {
                console.error('Failed to save self-assessment:', error);
                toast.error('Failed to save progress');
            }
        });
    }, [snapshotAssignment, getCurrentUser, executeSave]);

    // Handle continue button for each step
    const handleContinue = async () => {
        let updatedData = { ...selfAssessData };

        // Record timestamp for the current step
        const stepKey = `step_${interactiveStep}_completed_at` as keyof SelfAssessmentTimestamps;
        updatedData.timestamps = { ...updatedData.timestamps, [stepKey]: getHKTimestamp() };

        if (interactiveStep === 1) {
            // Q1: Expected correctness
            const totalQuestions = snapshotAssignment?.totalQuestions || 1;
            const ratio = (expectedCorrectCount ?? 0) / totalQuestions;
            updatedData.expected_correctness = ratio;
        } else if (interactiveStep === 2) {
            // Q2: Challenging knowledge points
            updatedData.challenging_kp = challengingKp;
            // If student self-assessed 100% correct, skip step 3 (primary mistakes) and set empty
            if (updatedData.expected_correctness !== undefined && updatedData.expected_correctness >= 1) {
                updatedData.primary_mistakes = [];
            }
        } else if (interactiveStep === 3) {
            // Q3: Primary mistakes
            updatedData.primary_mistakes = selectedMistakes;
        } else if (interactiveStep === 4) {
            // Phase 1 summary - no new data, just move on
        } else if (interactiveStep === 5) {
            // Q4: Efficiency satisfaction
            updatedData.efficiency_satisfaction = efficiencySatisfaction ?? undefined;
        } else if (interactiveStep === 6) {
            // Q5: Frequent solution - append "Others" text if selected, mark as completed
            const solutionArray = [...frequentSolution];
            // Find and update 'F. Others' entry with the actual text if provided
            const othersIndex = solutionArray.findIndex(s => s.startsWith('F.'));
            if (othersIndex !== -1 && frequentSolutionOther.trim()) {
                solutionArray[othersIndex] = `F. Others: ${frequentSolutionOther.trim()}`;
            }
            updatedData.frequent_solution = solutionArray;
            updatedData.completed = true;

            // Build payload for AI summary
            const summaryPayload = {
                assignment: {
                    id: snapshotAssignment?.assignment.assignment.id,
                    title: snapshotAssignment?.assignment.assignment.title,
                    topics: snapshotAssignment?.assignment.topicList,
                    knowledgePoints: snapshotAssignment?.knowledgePoints,
                    mistakedKnowledgePoints: snapshotAssignment?.mistakedKnowledgePoints,
                    primaryMistakes: snapshotAssignment?.primaryMistakes,
                    performance: {
                        totalQuestions: snapshotAssignment?.totalQuestions,
                        totalCorrect: snapshotAssignment?.totalCorrect,
                        totalDuration: snapshotAssignment?.totalDuration,
                    },
                    selfAssessment: {
                        expected_correctness: updatedData.expected_correctness,
                        challenging_kp: updatedData.challenging_kp,
                        primary_mistakes: updatedData.primary_mistakes,
                        efficiency_satisfaction: updatedData.efficiency_satisfaction,
                        frequent_solution: frequentSolution,
                    },
                },
            };

            if (previousAssignment) {
                summaryPayload['previousAssignment'] = {
                    id: previousAssignment?.assignment.assignment.id,
                    title: previousAssignment?.assignment.assignment.title,
                    topics: previousAssignment?.assignment.topicList,
                    knowledgePoints: previousAssignment?.knowledgePoints,
                    mistakedKnowledgePoints: previousAssignment?.mistakedKnowledgePoints,
                    primaryMistakes: previousAssignment?.primaryMistakes,
                    performance: {
                        totalQuestions: previousAssignment?.totalQuestions,
                        totalCorrect: previousAssignment?.totalCorrect,
                        totalDuration: previousAssignment?.totalDuration,
                    },
                };
            }

            // Fetch AI feedback and submit gamification points in parallel
            executeAiFeedback(async () => {
                try {
                    const studentId = getCurrentUser()?.id;
                    const gamificationCall = (!gamificationAlreadySubmitted && assignmentStatId)
                        ? submitSelfAssessment({
                            assignmentStatId,
                            answerSessionId: generateAnswerSessionToken({
                                studentId,
                                questionId: snapshotAssignment?.assignment.assignment.id,
                            }),
                        })
                        : Promise.resolve(null);

                    const [aiResult, gamResult] = await Promise.allSettled([
                        getStudentSummary({ chatId: String(studentId) + ":" + snapshotAssignment?.assignment.assignment.id, data: JSON.stringify(summaryPayload) }),
                        gamificationCall,
                    ]);

                    // Handle AI feedback
                    if (aiResult.status === 'fulfilled' && aiResult.value.code === 200 && aiResult.value.data?.summary) {
                        console.log('AI feedback received:', aiResult.value.data.summary);
                        updatedData.ai_feedback = aiResult.value.data.summary;
                    } else {
                        console.warn('AI feedback response not successful:', aiResult.status === 'fulfilled' ? aiResult.value : aiResult.reason);
                        updatedData.ai_feedback = t('selfAssess.aiInsightsFallback');
                    }

                    // Handle gamification result (silent fail — student still sees AI feedback)
                    if (gamResult.status === 'fulfilled' && gamResult.value) {
                        const gamRsp = gamResult.value;
                        if (gamRsp.code === 200 && gamRsp.data) {
                            setGamificationAlreadySubmitted(true);
                            const kpDelta = gamRsp.data.pointsAwardedKp ?? 0;
                            const gsDelta = gamRsp.data.pointsAwardedGs ?? 0;
                            const hasBoth = kpDelta > 0 && gsDelta > 0;
                            const spread = hasBoth ? 20 + Math.random() * 20 : 0;
                            if (kpDelta > 0) void flyKpToHeader({ delta: kpDelta, nextValue: kp + kpDelta, combined: hasBoth, xOffset: -spread }).then(() => addKp(kpDelta));
                            if (gsDelta > 0) void flyGsToHeader({ delta: gsDelta, nextValue: gs + gsDelta, combined: hasBoth, xOffset: spread }).then(() => addGs(gsDelta));
                        } else if (gamRsp.code === 6000) {
                            setGamificationAlreadySubmitted(true);
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch AI feedback:', error);
                    updatedData.ai_feedback = t('selfAssess.aiInsightsFallback');
                }
            });
        }

        setSelfAssessData(updatedData);
        await saveToBackend(updatedData);
        // Skip step 3 (primary mistakes) if student self-assessed 100% correct
        if (interactiveStep === 2 && updatedData.expected_correctness !== undefined && updatedData.expected_correctness >= 1) {
            setInteractiveStep(4); // jump to phase summary
        } else {
            setInteractiveStep(prev => prev + 1);
        }
    };

    const showRightPanel = stage !== 'select';
    const showSnapshot = (stage === 'snapshot' || stage === 'interactive' || stage === 'completed') && !!snapshotAssignment;
    // 右側面板的標題：完成態/復盤態切換為「互動式自我評估已完成」
    const rightPanelDone =
        stage === 'completed' || (stage === 'interactive' && interactiveStep === 7);

    // 互動式自我評估 — 「繼續 / 完成」按鈕（右下角）；僅 Q1~Q5 顯示
    const renderContinueButton = () => (
        <div className="flex justify-end pt-2">
            <button
                type="button"
                onClick={handleContinue}
                disabled={isSaving || isLoadingAiFeedback || !isCurrentStepValid()}
                className={cn(
                    'inline-flex items-center gap-2 rounded-[20px] px-5 py-2.5 text-sm font-semibold transition lg:px-6 lg:py-3 2xl:h-[54px] 2xl:text-lg min-[1920px]:h-[65px]! min-[1920px]:text-xl!',
                    'bg-[var(--practice-dialog-accent)] text-[var(--practice-dialog-accent-fg)] hover:brightness-95',
                    'disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer',
                )}
            >
                {isLoadingAiFeedback ? (
                    <>
                        <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        {t('selfAssess.loadingAiFeedback')}
                    </>
                ) : isSaving ? (
                    t('common.loading')
                ) : (
                    <>
                        {interactiveStep === 6 ? t('selfAssess.finishButton') : t('selfAssess.continueButton')}
                        <ArrowRight className="size-4" />
                    </>
                )}
            </button>
        </div>
    );

    if (isMobileLandscape) {
        return (
            <MobileLandscapeSelfAssess
                isLoading={isLoading}
                stage={stage}
                completedAssignments={completedAssignments}
                filteredAssignments={filteredAssignments}
                selectedAssignment={selectedAssignment}
                selectedAssignmentId={selectedAssignmentId}
                setSelectedAssignmentId={setSelectedAssignmentId}
                snapshotAssignment={snapshotAssignment}
                snapshotAssignmentId={snapshotAssignmentId}
                comparison={comparison}
                assignmentDropdownOpen={assignmentDropdownOpen}
                setAssignmentDropdownOpen={setAssignmentDropdownOpen}
                dateFilterOpen={dateFilterOpen}
                setDateFilterOpen={setDateFilterOpen}
                dateRange={dateRange}
                setDateRange={setDateRange}
                interactiveStep={interactiveStep}
                setInteractiveStep={setInteractiveStep}
                expectedCorrectCount={expectedCorrectCount}
                setExpectedCorrectCount={setExpectedCorrectCount}
                selectedMistakes={selectedMistakes}
                setSelectedMistakes={setSelectedMistakes}
                challengingKp={challengingKp}
                setChallengingKp={setChallengingKp}
                efficiencySatisfaction={efficiencySatisfaction}
                setEfficiencySatisfaction={setEfficiencySatisfaction}
                frequentSolution={frequentSolution}
                setFrequentSolution={setFrequentSolution}
                frequentSolutionOther={frequentSolutionOther}
                setFrequentSolutionOther={setFrequentSolutionOther}
                selfAssessData={selfAssessData}
                isSaving={isSaving}
                isLoadingAiFeedback={isLoadingAiFeedback}
                onStartAssessment={handleStartAssessment}
                onProceedInteractive={handleProceedInteractive}
                onContinue={handleContinue}
                isCurrentStepValid={isCurrentStepValid}
                onGoFreePractice={() => navigate(ROUTE.STUDENT.PRACTICE_HOME)}
            />
        );
    }

    return (
        <ContentLayout title={t('student.aibot')} backgroundColor="var(--practice-page-bg)" className="md:h-full md:overflow-hidden">
            {/* lg/xl 左 padding 降到 1920 的 pl-11(gap≈35,内容贴近侧栏)；2xl 锚 px-10 保持 1536 不变 */}
            <div className="mx-auto flex w-full max-w-[2000px] flex-1 flex-col px-6 pt-6 pb-6 lg:pl-[11px] xl:pr-10 2xl:px-10 min-[1920px]:pl-[11px]! md:min-h-0 md:overflow-y-auto">
                {/* 矮屏(<1000,笔记本/平板)门控:栅格行改 minmax(0,1fr)+右栏释放 min-h+卡片 padding/gap 收紧,三模块在一页内不溢出不裁切(1920×1080 高>1000 不触发,逐像素不变) */}
                {/* 列间距 lg/xl 收紧(24→12)右栏贴近左栏,xl+ 还原,1920 不变 */}
                <div
                    className={cn(
                        'grid w-full gap-6 lg:gap-x-3 xl:gap-x-6',
                        stage === 'select'
                            ? 'max-w-[703px] grid-cols-1'
                            : 'grid-cols-1 lg:grid-cols-2 lg:min-h-0 lg:flex-1 lg:[grid-template-rows:minmax(min-content,1fr)] [@media(max-height:1000px)]:[grid-template-rows:minmax(0,1fr)]!',
                    )}
                >
                    {/* ============ 左欄 ============ */}
                    <div className="flex min-w-0 flex-col gap-6 [@media(max-height:1000px)]:gap-3!">
                        {/* ---- 卡①：選擇作業 ---- */}
                        <div className="flex flex-col gap-3 rounded-[50px] bg-[#F3F3F4] p-6 lg:p-7 2xl:p-8 min-[1920px]:p-9! [@media(max-height:1000px)]:gap-2! [@media(max-height:1000px)]:p-4! [@media(max-height:1000px)]:rounded-[28px]!">
                            {/* 標題 */}
                            <div className="flex items-center gap-2 text-foreground">
                                <span
                                    className="inline-flex size-5 shrink-0 text-[var(--practice-dialog-accent)] 2xl:size-6 min-[1920px]:size-7! [&>svg]:size-full"
                                    dangerouslySetInnerHTML={{ __html: selectTaskIcon }}
                                />
                                <span className="text-base font-semibold lg:text-lg xl:text-2xl 2xl:text-[28px] min-[1920px]:text-4xl! min-[1920px]:font-medium!">{t('selfAssess.selectAssignment')}</span>
                            </div>

                            {isLoading ? (
                                <div className="py-8 text-center text-gray-500">{t('selfAssess.loading')}</div>
                            ) : completedAssignments.length === 0 ? (
                                <div className="py-8 text-center text-gray-500">{t('selfAssess.noCompletedAssignments')}</div>
                            ) : (
                                <>
                                    {selectedAssignment ? (
                                        <div
                                            key={selectedAssignmentId}
                                            className="flex min-w-0 items-center gap-3 animate-in fade-in slide-in-from-right-4 duration-300"
                                        >
                                            <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-[#E1F1D3] text-[#006532] lg:size-[43px]">
                                                <span
                                                    className="inline-flex size-4 shrink-0 lg:size-5 [&>svg]:size-full"
                                                    dangerouslySetInnerHTML={{ __html: subjectBookIcon }}
                                                />
                                            </span>
                                            <span className="truncate text-base font-semibold text-foreground lg:text-lg 2xl:text-xl min-[1920px]:text-2xl!">
                                                {selectedAssignment.assignment.assignment.title}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3 text-gray-400">
                                            <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-gray-100 lg:size-[43px]">
                                                <span
                                                    className="inline-flex size-4 shrink-0 lg:size-5 [&>svg]:size-full"
                                                    dangerouslySetInnerHTML={{ __html: subjectBookIcon }}
                                                />
                                            </span>
                                            <span className="text-sm">{t('selfAssess.selectPrompt')}</span>
                                        </div>
                                    )}

                                    {selectedAssignment && (
                                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-500 lg:text-[13px] 2xl:text-sm min-[1920px]:text-base!">
                                            <span className="flex items-center gap-1.5">
                                                <span
                                                    className="inline-flex size-3.5 shrink-0 min-[1920px]:size-[15px]! [&>svg]:size-full"
                                                    dangerouslySetInnerHTML={{ __html: deadlineCalIcon }}
                                                />
                                                {t('selfAssess.deadline')}:{selectedAssignment.assignment.assignment.dueDate
                                                    ? format(new Date(selectedAssignment.assignment.assignment.dueDate), t('selfAssess.dateFormatFull'))
                                                    : t('selfAssess.noDueDateSet')}
                                            </span>
                                            {selectedAssignment.completionDate && (
                                                <span className="flex items-center gap-1.5 text-[#006532]">
                                                    <span
                                                        className="inline-flex size-3.5 shrink-0 min-[1920px]:size-[15px]! [&>svg]:size-full"
                                                        dangerouslySetInnerHTML={{ __html: submitCalIcon }}
                                                    />
                                                    {t('selfAssess.completedOn')}:{format(selectedAssignment.completionDate, t('selfAssess.dateFormatFull'))}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* 操作行：選擇其他作業（左，白底邊框）+ 開始自我評估（右，主色） */}
                                    <div className="mt-2 flex items-center gap-3">
                                        <Popover
                                            open={assignmentDropdownOpen}
                                            onOpenChange={(open) => {
                                                if (!open) setDateFilterOpen(false);
                                                setAssignmentDropdownOpen(open);
                                            }}
                                        >
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    className="flex flex-1 cursor-pointer items-center justify-between gap-2 rounded-[14px] border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-gray-50 lg:py-3 2xl:h-[54px] 2xl:rounded-[17px] 2xl:text-lg min-[1920px]:h-[65px]! min-[1920px]:rounded-[20px]! min-[1920px]:text-xl!"
                                                >
                                                    {t('selfAssess.selectOther')}
                                                    <ChevronDown className="size-4 text-gray-500" />
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0" align="start">
                                                {/* 日期篩選頭 */}
                                                <div className="flex items-center justify-between border-b p-3">
                                                    <span className="text-sm font-medium text-foreground">{t('selfAssess.dateFilter')}</span>
                                                    <Popover open={dateFilterOpen} onOpenChange={setDateFilterOpen}>
                                                        <PopoverTrigger asChild>
                                                            <button
                                                                type="button"
                                                                className={cn(
                                                                    'flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2 text-xs font-medium transition hover:bg-gray-100',
                                                                    (dateRange.from || dateRange.to) ? 'text-foreground' : 'text-gray-500',
                                                                )}
                                                            >
                                                                <CalendarIcon className="size-3.5" />
                                                                {dateRange.from ? (
                                                                    dateRange.to
                                                                        ? <>{format(dateRange.from, 'MM/dd')} - {format(dateRange.to, 'MM/dd')}</>
                                                                        : format(dateRange.from, 'MM/dd')
                                                                ) : t('selfAssess.allDates')}
                                                            </button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0" align="end">
                                                            <Calendar
                                                                mode="range"
                                                                selected={{ from: dateRange.from, to: dateRange.to }}
                                                                onSelect={(range) => setDateRange({ from: range?.from, to: range?.to })}
                                                                numberOfMonths={1}
                                                                classNames={CALENDAR_CLASSNAMES}
                                                            />
                                                            <div className="flex gap-2 border-t p-2">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => setDateRange({})}
                                                                    className="flex-1 cursor-pointer text-xs"
                                                                >
                                                                    {t('selfAssess.clearFilter')}
                                                                </Button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setDateFilterOpen(false)}
                                                                    className="flex-1 cursor-pointer rounded-md bg-[var(--practice-dialog-accent)] px-3 py-1.5 text-xs font-semibold text-[var(--practice-dialog-accent-fg)] hover:brightness-95"
                                                                >
                                                                    {t('component.confirm')}
                                                                </button>
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>

                                                <div className="max-h-[300px] overflow-y-auto p-1">
                                                    {filteredAssignments.length === 0 ? (
                                                        <div className="p-4 text-center text-sm text-gray-500">{t('selfAssess.noAssignmentsInRange')}</div>
                                                    ) : (
                                                        filteredAssignments.map((a) => {
                                                            const isSel = selectedAssignmentId === a.assignment.assignment.id?.toString();
                                                            return (
                                                                <div
                                                                    key={a.assignment.assignment.id}
                                                                    onClick={() => {
                                                                        setSelectedAssignmentId(a.assignment.assignment.id?.toString() || '');
                                                                        setAssignmentDropdownOpen(false);
                                                                    }}
                                                                    className={cn(
                                                                        'cursor-pointer rounded-[15px] p-3 transition-colors',
                                                                        isSel
                                                                            ? 'bg-[color-mix(in_srgb,var(--practice-dialog-accent)_30%,white)]'
                                                                            : 'hover:bg-gray-50',
                                                                    )}
                                                                >
                                                                    <div className="text-sm font-semibold text-foreground">
                                                                        {a.assignment.assignment.title}
                                                                    </div>
                                                                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px] text-gray-500">
                                                                        <span>
                                                                            {t('selfAssess.deadline')}:{a.assignment.assignment.dueDate
                                                                                ? format(new Date(a.assignment.assignment.dueDate), 'yyyy年MM月dd日')
                                                                                : t('selfAssess.notSet')}
                                                                        </span>
                                                                        {a.completionDate && (
                                                                            <span className="text-[#5FA800]">
                                                                                {t('selfAssess.completedOn')}:{format(a.completionDate, 'yyyy年MM月dd日')}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </PopoverContent>
                                        </Popover>

                                        <button
                                            type="button"
                                            onClick={handleStartAssessment}
                                            disabled={!selectedAssignmentId || (stage !== 'select' && selectedAssignmentId === snapshotAssignmentId)}
                                            className={cn(
                                                'flex flex-1 items-center justify-between gap-2 rounded-[14px] px-4 py-2.5 text-sm font-medium transition lg:py-3 2xl:h-[54px] 2xl:rounded-[17px] 2xl:text-lg min-[1920px]:h-[65px]! min-[1920px]:rounded-[20px]! min-[1920px]:text-xl!',
                                                'bg-[var(--practice-dialog-accent)] text-[var(--practice-dialog-accent-fg)] hover:brightness-95',
                                                'disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer',
                                            )}
                                        >
                                            {t('selfAssess.startAssessment')}
                                            <ArrowRight className="size-4" />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* ---- 卡②：作業快照（snapshot/interactive/completed 才顯示） ---- */}
                        {showSnapshot && snapshotAssignment && (
                            <div
                                key={snapshotAssignmentId}
                                className="flex flex-col gap-4 rounded-[50px] bg-[#F3F3F4] p-6 lg:p-7 2xl:p-8 min-[1920px]:p-9! lg:flex-1 [@media(max-height:1000px)]:gap-2! [@media(max-height:1000px)]:p-4! [@media(max-height:1000px)]:min-h-0 [@media(max-height:1000px)]:rounded-[28px]! animate-in fade-in slide-in-from-bottom-4 duration-300"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 text-foreground">
                                        <span
                                            className="inline-flex size-5 shrink-0 text-[var(--practice-dialog-accent)] 2xl:size-6 min-[1920px]:size-7! [&>svg]:size-full"
                                            dangerouslySetInnerHTML={{ __html: snapshotIcon }}
                                        />
                                        <span className="text-base font-semibold lg:text-lg xl:text-2xl 2xl:text-[28px] min-[1920px]:text-4xl! min-[1920px]:font-medium! max-2xl:[@media(max-height:1000px)]:text-base!">{t('selfAssess.snapshot')}</span>
                                    </div>
                                    <span className="truncate text-sm text-gray-500 xl:text-base 2xl:text-lg min-[1920px]:text-2xl! min-[1920px]:text-[#1A1C1D]! min-[1920px]:font-[450]!">
                                        {snapshotAssignment.assignment.assignment.title}
                                    </span>
                                </div>

                                {/* 矮屏:lg/xl 仍走 auto-rows-fr 填充(同 1920,色块满板不溢出不留白);仅 2xl(1536)保留固定 aspect 不变 */}
                                <div className="grid grid-cols-2 gap-3 lg:gap-4 lg:flex-1 lg:auto-rows-fr [@media(max-height:1000px)]:gap-2! 2xl:[@media(max-height:1000px)]:flex-none! 2xl:[@media(max-height:1000px)]:auto-rows-auto!">
                                    {/* 成績 — 淺藍 */}
                                    <div className="flex flex-col justify-between gap-2 rounded-[20px] p-4 lg:p-5 [@media(max-height:1000px)]:p-3! 2xl:[@media(max-height:1000px)]:aspect-[302/199]!" style={{ backgroundColor: '#B6E2EF' }}>
                                        <div className="flex items-center gap-2 text-xs font-medium text-[#82A3AD] lg:text-sm 2xl:text-base min-[1920px]:text-xl! max-2xl:[@media(max-height:1000px)]:text-xs!">
                                            <span
                                                className="inline-flex size-4 shrink-0 2xl:size-5 min-[1920px]:size-6! [&>svg]:size-full"
                                                dangerouslySetInnerHTML={{ __html: scoreIcon }}
                                            />
                                            <span>{t('selfAssess.score')}</span>
                                        </div>
                                        <p className="text-xl font-bold text-foreground lg:text-lg xl:text-2xl 2xl:text-[28px] min-[1920px]:text-[32px]! max-2xl:[@media(max-height:1000px)]:text-xl!">
                                            {snapshotAssignment.totalCorrect}/{snapshotAssignment.totalQuestions}
                                            {' '}
                                            ({Math.round((snapshotAssignment.totalCorrect / Math.max(1, snapshotAssignment.totalQuestions)) * 100)}%)
                                        </p>
                                    </div>

                                    {/* 完成時間 — 淺黃 */}
                                    <div className="flex flex-col justify-between gap-2 rounded-[20px] p-4 lg:p-5 [@media(max-height:1000px)]:p-3! 2xl:[@media(max-height:1000px)]:aspect-[302/199]!" style={{ backgroundColor: '#F8EFA8' }}>
                                        <div className="flex items-center gap-2 text-xs font-medium text-[#DAC62E] lg:text-sm 2xl:text-base min-[1920px]:text-xl! max-2xl:[@media(max-height:1000px)]:text-xs!">
                                            <span
                                                className="inline-flex size-4 shrink-0 2xl:size-5 min-[1920px]:size-6! [&>svg]:size-full"
                                                dangerouslySetInnerHTML={{ __html: timeIcon }}
                                            />
                                            <span>{t('selfAssess.completionTime')}</span>
                                        </div>
                                        <p className="text-xl font-bold text-foreground lg:text-lg xl:text-2xl 2xl:text-[28px] min-[1920px]:text-[32px]! max-2xl:[@media(max-height:1000px)]:text-xl!">
                                            {snapshotAssignment.totalDuration >= 60
                                                ? `${Math.floor(snapshotAssignment.totalDuration / 60)}${t('selfAssess.minutes')}${snapshotAssignment.totalDuration % 60}${t('selfAssess.seconds')}`
                                                : `${snapshotAssignment.totalDuration}${t('selfAssess.seconds')}`}
                                        </p>
                                    </div>

                                    {/* 知識點 — 淺粉 (顯示「掌握/總計 (P%)」：總 = 全部 KP，掌握 = 不在錯題 KP 中) */}
                                    <div className="flex flex-col justify-between gap-2 rounded-[20px] p-4 lg:p-5 [@media(max-height:1000px)]:p-3! 2xl:[@media(max-height:1000px)]:aspect-[302/199]!" style={{ backgroundColor: '#F8D1C3' }}>
                                        <div className="flex items-center gap-2 text-xs font-medium text-[#A6887E] lg:text-sm 2xl:text-base min-[1920px]:text-xl! max-2xl:[@media(max-height:1000px)]:text-xs!">
                                            <span
                                                className="inline-flex size-4 shrink-0 2xl:size-5 min-[1920px]:size-6! [&>svg]:size-full"
                                                dangerouslySetInnerHTML={{ __html: knowledgeIcon }}
                                            />
                                            <span>{t('selfAssess.knowledgePoints')}</span>
                                        </div>
                                        {(() => {
                                            const total = snapshotAssignment.knowledgePoints?.length ?? 0;
                                            const mistaked = snapshotAssignment.mistakedKnowledgePoints?.length ?? 0;
                                            const mastered = Math.max(0, total - mistaked);
                                            return (
                                                <p className="text-xl font-bold text-foreground lg:text-lg xl:text-2xl 2xl:text-[28px] min-[1920px]:text-[32px]! max-2xl:[@media(max-height:1000px)]:text-xl!">
                                                    {total === 0 ? '-' : `${mastered}/${total} (${Math.round((mastered / total) * 100)}%)`}
                                                </p>
                                            );
                                        })()}
                                    </div>

                                    {/* 與上次作業比較 — 淺綠 */}
                                    <div className="flex flex-col justify-between gap-2 rounded-[20px] p-4 lg:p-5 [@media(max-height:1000px)]:p-3! 2xl:[@media(max-height:1000px)]:aspect-[302/199]!" style={{ backgroundColor: '#E5F8E4' }}>
                                        <div className="flex items-center gap-2 text-xs font-medium text-[#A3BDA2] lg:text-sm 2xl:text-base min-[1920px]:text-xl! max-2xl:[@media(max-height:1000px)]:text-xs!">
                                            {comparison?.improved ? (
                                                <span
                                                    className="inline-flex size-4 shrink-0 2xl:size-5 min-[1920px]:size-6! [&>svg]:size-full"
                                                    dangerouslySetInnerHTML={{ __html: trendUpIcon }}
                                                />
                                            ) : comparison?.declined ? (
                                                <TrendingDown className="size-4 2xl:size-5 min-[1920px]:size-6!" />
                                            ) : (
                                                <Minus className="size-4 2xl:size-5 min-[1920px]:size-6!" />
                                            )}
                                            <span>{t('selfAssess.comparisonTitle')}</span>
                                        </div>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-xl font-bold text-foreground lg:text-lg xl:text-2xl 2xl:text-[28px] min-[1920px]:text-[32px]! max-2xl:[@media(max-height:1000px)]:text-xl!">
                                                {comparison ? `${comparison.diff > 0 ? '+' : ''}${comparison.diff.toFixed(0)}%` : '-'}
                                            </span>
                                            {comparison && (
                                                <span className="text-xs text-foreground lg:text-sm 2xl:text-base min-[1920px]:text-xl! max-2xl:[@media(max-height:1000px)]:text-xs!">
                                                    {comparison.improved ? t('selfAssess.improved') : comparison.declined ? t('selfAssess.declined') : t('selfAssess.stable')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ============ 右欄：互動式自我評估 / 完成態 ============ */}
                    {showRightPanel && (
                        <div className="flex min-w-0 flex-col">
                            <div className="flex h-full min-h-[480px] flex-col gap-4 rounded-[50px] bg-[#F3F3F4] p-6 lg:min-h-[640px] lg:p-7 2xl:min-h-[720px] min-[1920px]:min-h-[817px]! min-[1920px]:p-9! [@media(max-height:1000px)]:min-h-0! [@media(max-height:1000px)]:gap-2! [@media(max-height:1000px)]:p-4! [@media(max-height:1000px)]:rounded-[28px]!">
                                <div className="flex items-center gap-2 text-foreground">
                                    <span
                                        className="inline-flex size-5 shrink-0 text-[var(--practice-dialog-accent)] 2xl:w-[23px] 2xl:h-[24px] min-[1920px]:w-[26px]! min-[1920px]:h-[27px]! [&>svg]:size-full"
                                        dangerouslySetInnerHTML={{ __html: sparkleIcon }}
                                    />
                                    <span className="text-base font-semibold lg:text-lg xl:text-2xl 2xl:text-[28px] min-[1920px]:text-4xl! min-[1920px]:font-medium!">
                                        {rightPanelDone ? t('selfAssess.completedTitle') : t('selfAssess.interactiveSelfAssessment')}
                                    </span>
                                </div>

                                {/* 內容區（按 stage / step 切換） */}
                                <div className="flex min-h-0 flex-1 flex-col gap-4">
                                    {/* —— snapshot 階段：提示卡 —— */}
                                    {stage === 'snapshot' && (
                                        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-2 text-center">
                                            <h3 className="text-xl font-semibold leading-snug text-foreground lg:text-2xl">
                                                {t('selfAssess.interactivePromptTitle')}
                                            </h3>
                                            <p className="max-w-[320px] text-sm text-gray-500">
                                                {t('selfAssess.interactivePromptDesc')}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={handleProceedInteractive}
                                                className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-[20px] bg-[var(--practice-dialog-accent)] px-6 py-3 text-sm font-semibold text-[var(--practice-dialog-accent-fg)] transition hover:brightness-95 2xl:h-[54px] 2xl:text-lg min-[1920px]:h-[65px]! min-[1920px]:text-xl!"
                                            >
                                                {t('selfAssess.startInteractive')}
                                                <ArrowRight className="size-4" />
                                            </button>
                                        </div>
                                    )}

                                    {/* —— interactive 階段：Q1~Q5、Phase 總結、Final 總結 + AI —— */}
                                    {stage === 'interactive' && snapshotAssignment && (
                                        <>
                                            {interactiveStep === 1 && (
                                                <div className="flex flex-1 flex-col gap-4">
                                                    <h3 className="text-base font-medium text-foreground lg:text-lg">{t('selfAssess.q1Title')}</h3>
                                                    <p className="text-sm text-gray-500">{t('selfAssess.q1Desc')}</p>
                                                    <div className="flex flex-1 flex-col gap-4 py-2" onPointerDown={() => expectedCorrectCount === null && setExpectedCorrectCount(0)}>
                                                        <Slider
                                                            value={[expectedCorrectCount ?? 0]}
                                                            onValueChange={(val) => setExpectedCorrectCount(val[0])}
                                                            min={0}
                                                            max={snapshotAssignment.totalQuestions}
                                                            step={1}
                                                            className={SLIDER_CLASS}
                                                        />
                                                        <div className="flex justify-between text-sm text-gray-500">
                                                            <span>0</span>
                                                            <span>{snapshotAssignment.totalQuestions}</span>
                                                        </div>
                                                        <div className="rounded-[20px] border border-gray-200 bg-white px-4 py-3 text-center">
                                                            {expectedCorrectCount === null ? (
                                                                <span className="text-sm text-gray-400">{t('selfAssess.scaleNotSelected')}</span>
                                                            ) : (
                                                                <span className="text-base font-semibold text-foreground">
                                                                    {t('selfAssess.q1Format', {
                                                                        x: expectedCorrectCount,
                                                                        y: snapshotAssignment.totalQuestions,
                                                                        percent: Math.round((expectedCorrectCount / Math.max(1, snapshotAssignment.totalQuestions)) * 100),
                                                                    })}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {interactiveStep === 2 && (
                                                <div className="flex flex-1 flex-col gap-3">
                                                    <h3 className="text-base font-medium text-foreground lg:text-lg">{t('selfAssess.q2Title')}</h3>
                                                    <p className="text-sm text-gray-500">{t('selfAssess.q2Hint')}</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {(snapshotAssignment.knowledgePoints && snapshotAssignment.knowledgePoints.length > 0
                                                            ? snapshotAssignment.knowledgePoints
                                                            : snapshotAssignment.assignment.topicList
                                                        ).map((kp, idx) => (
                                                            <button
                                                                type="button"
                                                                key={idx}
                                                                onClick={() => setChallengingKp(prev => prev ? `${prev}, ${kp}` : kp)}
                                                                className="cursor-pointer rounded-[20px] border border-[color-mix(in_srgb,var(--practice-dialog-accent)_55%,transparent)] bg-[color-mix(in_srgb,var(--practice-dialog-accent)_18%,white)] px-3 py-1.5 text-xs text-foreground transition hover:brightness-95 lg:text-sm"
                                                            >
                                                                {kp}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <Textarea
                                                        value={challengingKp}
                                                        onChange={(e) => setChallengingKp(e.target.value)}
                                                        placeholder={t('selfAssess.q2Placeholder')}
                                                        className="min-h-[120px] lg:min-h-[150px] 2xl:min-h-[160px] min-[1920px]:min-h-[168px]! rounded-[16px] border-gray-200 bg-white text-sm"
                                                    />
                                                </div>
                                            )}

                                            {interactiveStep === 3 && (
                                                <div className="flex flex-1 flex-col gap-3">
                                                    <h3 className="text-base font-medium text-foreground lg:text-lg">{t('selfAssess.q3Title')}</h3>
                                                    <p className="text-sm text-gray-500">{t('selfAssess.q3Hint')}</p>
                                                    <div className="flex flex-1 flex-col gap-2.5 overflow-y-auto pr-1">
                                                        {MISTAKE_OPTIONS.map((option) => {
                                                            const checked = selectedMistakes.includes(option.key);
                                                            return (
                                                                <label
                                                                    key={option.key}
                                                                    className={cn(
                                                                        'flex cursor-pointer items-start gap-3 rounded-[20px] border p-3 transition lg:p-4',
                                                                        checked
                                                                            ? 'border-[var(--practice-dialog-accent)] bg-[color-mix(in_srgb,var(--practice-dialog-accent)_15%,white)]'
                                                                            : 'border-gray-200 hover:border-gray-300',
                                                                    )}
                                                                >
                                                                    <Checkbox
                                                                        className={cn('mt-0.5', CHECKBOX_CLASS)}
                                                                        checked={checked}
                                                                        onCheckedChange={(c) => {
                                                                            if (c) setSelectedMistakes(prev => [...prev, option.key]);
                                                                            else setSelectedMistakes(prev => prev.filter(m => m !== option.key));
                                                                        }}
                                                                    />
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="text-sm font-medium text-foreground">{t(`selfAssess.${option.translationKey}`)}</div>
                                                                        <div className="text-xs text-gray-500">{t(`selfAssess.${option.explanationKey}`)}</div>
                                                                    </div>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {interactiveStep === 4 && (
                                                <div className="flex flex-1 flex-col gap-3">
                                                    <h3 className="text-base font-medium text-foreground lg:text-lg">{t('selfAssess.phaseSummaryTitle')}</h3>
                                                    <div className="overflow-x-auto rounded-[16px] border border-gray-200">
                                                        <table className="w-full border-collapse text-xs lg:text-sm">
                                                            <thead>
                                                                <tr className="bg-gray-50 text-left text-gray-700">
                                                                    <th className="p-3 font-medium">{t('selfAssess.phaseSummaryQuestion')}</th>
                                                                    <th className="p-3 font-medium">{t('selfAssess.phaseSummaryYourAnswer')}</th>
                                                                    <th className="p-3 font-medium">{t('selfAssess.phaseSummaryActual')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="text-foreground">
                                                                <tr className="border-t border-gray-200">
                                                                    <td className="p-3 align-top">{t('selfAssess.q1Title')}</td>
                                                                    <td className="p-3 align-top">
                                                                        {selfAssessData.expected_correctness !== undefined
                                                                            ? `${Math.round(selfAssessData.expected_correctness * 100)}%`
                                                                            : '-'}
                                                                    </td>
                                                                    <td className="p-3 align-top">
                                                                        {snapshotAssignment && snapshotAssignment.totalQuestions > 0
                                                                            ? `${Math.round((snapshotAssignment.totalCorrect / snapshotAssignment.totalQuestions) * 100)}%`
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                                <tr className="border-t border-gray-200">
                                                                    <td className="p-3 align-top">{t('selfAssess.q2Title')}</td>
                                                                    <td className="p-3 align-top">{selfAssessData.challenging_kp || '-'}</td>
                                                                    <td className="p-3 align-top">
                                                                        {snapshotAssignment?.mistakedKnowledgePoints && snapshotAssignment.mistakedKnowledgePoints.length > 0
                                                                            ? snapshotAssignment.mistakedKnowledgePoints.slice(0, 3).join(', ')
                                                                            : t('selfAssess.noMistakedKpMessage')}
                                                                    </td>
                                                                </tr>
                                                                <tr className="border-t border-gray-200">
                                                                    <td className="p-3 align-top">{t('selfAssess.q3Title')}</td>
                                                                    <td className="p-3 align-top">
                                                                        {selfAssessData.primary_mistakes && selfAssessData.primary_mistakes.length > 0
                                                                            ? selfAssessData.primary_mistakes.map(m => {
                                                                                const option = MISTAKE_OPTIONS.find(opt => opt.key === m);
                                                                                return option ? t(`selfAssess.${option.translationKey}`) : m;
                                                                            }).join(', ')
                                                                            : Array.isArray(selfAssessData.primary_mistakes)
                                                                                ? t('selfAssess.noMistakes')
                                                                                : '-'}
                                                                    </td>
                                                                    <td className="p-3 align-top">
                                                                        {snapshotAssignment?.primaryMistakes && snapshotAssignment.primaryMistakes.length > 0
                                                                            ? questionMistakeI18N(t, snapshotAssignment.primaryMistakes[0])
                                                                            : Array.isArray(snapshotAssignment?.primaryMistakes)
                                                                                ? t('selfAssess.noMistakes')
                                                                                : t('selfAssess.phaseSummaryPlaceholder')}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {interactiveStep === 5 && (
                                                <div className="flex flex-1 flex-col gap-4">
                                                    <h3 className="text-base font-medium text-foreground lg:text-lg">{t('selfAssess.q4Title')}</h3>
                                                    <div className="flex flex-1 flex-col gap-4 py-2" onPointerDown={() => efficiencySatisfaction === null && setEfficiencySatisfaction(3)}>
                                                        <Slider
                                                            value={[efficiencySatisfaction ?? 3]}
                                                            onValueChange={(val) => setEfficiencySatisfaction(val[0])}
                                                            min={1}
                                                            max={5}
                                                            step={1}
                                                            className={SLIDER_CLASS}
                                                        />
                                                        <div className="flex justify-between text-xs text-gray-500 lg:text-sm">
                                                            <span>{t('selfAssess.q4Level1')}</span>
                                                            <span>{t('selfAssess.q4Level3')}</span>
                                                            <span>{t('selfAssess.q4Level5')}</span>
                                                        </div>
                                                        <div className="rounded-[20px] border border-gray-200 bg-white px-4 py-3 text-center">
                                                            {efficiencySatisfaction === null ? (
                                                                <span className="text-sm text-gray-400">{t('selfAssess.scaleNotSelected')}</span>
                                                            ) : (
                                                                <span className="text-base font-semibold text-foreground">
                                                                    {efficiencySatisfaction} / 5 — {t(`selfAssess.q4Level${efficiencySatisfaction}`)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {interactiveStep === 6 && (
                                                <div className="flex flex-1 flex-col gap-3">
                                                    <h3 className="text-base font-medium text-foreground lg:text-lg">{t('selfAssess.q5Title')}</h3>
                                                    <p className="text-sm text-gray-500">{t('selfAssess.q5Hint')}</p>
                                                    <div className="flex flex-1 flex-col gap-2.5 overflow-y-auto pr-1">
                                                        {SOLUTION_OPTIONS.map((option) => {
                                                            const optionText = `${option.key}. ${t(`selfAssess.${option.translationKey}`)}`;
                                                            const isSel = frequentSolution.some(s => s.startsWith(`${option.key}.`));
                                                            return (
                                                                <label
                                                                    key={option.key}
                                                                    className={cn(
                                                                        'flex cursor-pointer items-center gap-3 rounded-[20px] border p-3 transition lg:p-4',
                                                                        isSel
                                                                            ? 'border-[var(--practice-dialog-accent)] bg-[color-mix(in_srgb,var(--practice-dialog-accent)_15%,white)]'
                                                                            : 'border-gray-200 hover:border-gray-300',
                                                                    )}
                                                                >
                                                                    <Checkbox
                                                                        className={CHECKBOX_CLASS}
                                                                        checked={isSel}
                                                                        onCheckedChange={(c) => {
                                                                            if (c) setFrequentSolution(prev => [...prev, optionText]);
                                                                            else {
                                                                                setFrequentSolution(prev => prev.filter(s => !s.startsWith(`${option.key}.`)));
                                                                                if (option.key === 'F') setFrequentSolutionOther('');
                                                                            }
                                                                        }}
                                                                    />
                                                                    <span className="text-sm font-medium text-foreground">{t(`selfAssess.${option.translationKey}`)}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                    {frequentSolution.some(s => s.startsWith('F.')) && (
                                                        <Textarea
                                                            value={frequentSolutionOther}
                                                            onChange={(e) => setFrequentSolutionOther(e.target.value)}
                                                            placeholder={t('selfAssess.q5OtherPlaceholder')}
                                                            className="mt-2 min-h-[80px] rounded-[16px] border-gray-200 bg-white text-sm"
                                                        />
                                                    )}
                                                </div>
                                            )}

                                            {interactiveStep === 7 && (
                                                <div className="flex flex-1 flex-col gap-4">
                                                    <h3 className="text-base font-medium text-foreground lg:text-lg">{t('selfAssess.phaseSummaryTitle')}</h3>
                                                    <div className="overflow-x-auto rounded-[16px] border border-gray-200">
                                                        <table className="w-full border-collapse text-xs lg:text-sm">
                                                            <thead>
                                                                <tr className="bg-gray-50 text-left text-gray-700">
                                                                    <th className="p-3 font-medium">{t('selfAssess.phaseSummaryQuestion')}</th>
                                                                    <th className="p-3 font-medium">{t('selfAssess.phaseSummaryYourAnswer')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="text-foreground">
                                                                <tr className="border-t border-gray-200">
                                                                    <td className="p-3 align-top">{t('selfAssess.q1Title')}</td>
                                                                    <td className="p-3 align-top">
                                                                        {selfAssessData.expected_correctness !== undefined
                                                                            ? `${Math.round(selfAssessData.expected_correctness * 100)}%`
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                                <tr className="border-t border-gray-200">
                                                                    <td className="p-3 align-top">{t('selfAssess.q2Title')}</td>
                                                                    <td className="p-3 align-top">{selfAssessData.challenging_kp || '-'}</td>
                                                                </tr>
                                                                <tr className="border-t border-gray-200">
                                                                    <td className="p-3 align-top">{t('selfAssess.q3Title')}</td>
                                                                    <td className="p-3 align-top">
                                                                        {selfAssessData.primary_mistakes && selfAssessData.primary_mistakes.length > 0
                                                                            ? selfAssessData.primary_mistakes.join(', ')
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                                <tr className="border-t border-gray-200">
                                                                    <td className="p-3 align-top">{t('selfAssess.q4Title')}</td>
                                                                    <td className="p-3 align-top">
                                                                        {selfAssessData.efficiency_satisfaction
                                                                            ? `${selfAssessData.efficiency_satisfaction}/5`
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                                <tr className="border-t border-gray-200">
                                                                    <td className="p-3 align-top">{t('selfAssess.q5Title')}</td>
                                                                    <td className="p-3 align-top">
                                                                        {selfAssessData.frequent_solution && selfAssessData.frequent_solution.length > 0
                                                                            ? selfAssessData.frequent_solution.join(', ')
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    <div
                                                        className="flex flex-col gap-2 rounded-[20px] p-4 lg:p-5"
                                                        style={{ backgroundColor: 'color-mix(in srgb, var(--practice-dialog-accent) 18%, white)' }}
                                                    >
                                                        <div className="flex items-center gap-2 text-foreground">
                                                            <Sparkles className="size-4 text-[var(--practice-dialog-accent)]" fill="currentColor" />
                                                            <span className="text-sm font-semibold">{t('selfAssess.aiInsightsTitle')}</span>
                                                        </div>
                                                        {isLoadingAiFeedback ? (
                                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                                <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                                                <span>{t('selfAssess.loadingAiFeedback')}</span>
                                                            </div>
                                                        ) : (
                                                            <p className="whitespace-pre-wrap text-sm text-foreground/80">
                                                                {selfAssessData.ai_feedback || t('selfAssess.aiInsightsFallback')}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {stage === 'completed' && snapshotAssignment && (
                                        <>
                                            {interactiveStep !== 7 && (
                                                <div className="flex flex-1 flex-col items-center justify-center gap-3 px-2 text-center">
                                                    <h3 className="whitespace-pre-line text-xl font-semibold leading-snug text-foreground lg:text-2xl">
                                                        {t('selfAssess.completedMessage')}
                                                    </h3>
                                                    <p className="max-w-[320px] text-sm text-gray-500">
                                                        {t('selfAssess.completedDesc')}
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={() => setInteractiveStep(7)}
                                                        className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-[20px] bg-[var(--practice-dialog-accent)] px-6 py-3 text-sm font-medium text-[var(--practice-dialog-accent-fg)] transition hover:brightness-95 2xl:h-[54px] 2xl:text-lg min-[1920px]:h-[65px]! min-[1920px]:text-xl!"
                                                    >
                                                        {t('selfAssess.revisitFeedback')}
                                                        <Sparkles className="size-4" fill="currentColor" />
                                                    </button>
                                                </div>
                                            )}

                                            {interactiveStep === 7 && (
                                                <div
                                                    className="flex flex-col gap-2 rounded-[40px] p-4 lg:p-5"
                                                    style={{ backgroundColor: 'color-mix(in srgb, var(--practice-dialog-accent) 18%, white)' }}
                                                >
                                                    <div className="flex items-center gap-2 text-foreground">
                                                        <Sparkles className="size-4 text-[var(--practice-dialog-accent)]" fill="currentColor" />
                                                        <span className="text-sm font-semibold">{t('selfAssess.aiInsightsTitle')}</span>
                                                    </div>
                                                    <p className="whitespace-pre-wrap text-sm text-foreground/80">
                                                        {selfAssessData.ai_feedback || t('selfAssess.aiInsightsPlaceholder')}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* 底部按鈕：interactive Q1~Q5 顯示「繼續/完成」；step7 與 completed 顯示「前往自由練習」 */}
                                {stage === 'interactive' && interactiveStep <= 6 && renderContinueButton()}
                                {((stage === 'interactive' && interactiveStep === 7) ||
                                    (stage === 'completed' && interactiveStep === 7)) && (
                                    <div className="flex justify-end pt-2">
                                        <button
                                            type="button"
                                            onClick={() => navigate(ROUTE.STUDENT.PRACTICE_HOME)}
                                            className="inline-flex cursor-pointer items-center gap-2 rounded-[20px] bg-[var(--practice-dialog-accent)] px-5 py-2.5 text-sm font-semibold text-[var(--practice-dialog-accent-fg)] transition hover:brightness-95 lg:px-6 lg:py-3 2xl:h-[54px] 2xl:text-lg min-[1920px]:h-[65px]! min-[1920px]:text-xl!"
                                        >
                                            {t('selfAssess.goFreePractice')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ContentLayout>
    );
}
